import React, { useState, useEffect, useCallback, useRef } from "react";
import { Stack, Alert } from "@mui/material";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";

import { DashboardHeader } from "../shared";
import {
  getAdminUserStats,
  getAdminUsersByRole,
  getAdminAuditSummary24h,
  getAdminRecentActivity,
  getAdminOnlineUsers,
} from "../../../../utils/services/control/adminDashboardService";
import UserStatsWidget from "./dashboard/UserStatsWidget";
import UsersByRoleWidget from "./dashboard/UsersByRoleWidget";
import AuditSummaryWidget from "./dashboard/AuditSummaryWidget";
import RecentActivityWidget from "./dashboard/RecentActivityWidget";
import OnlineUsersWidget from "./dashboard/OnlineUsersWidget";
import InactiveUsersWidget from "./dashboard/InactiveUsersWidget";

const ONLINE_REFRESH_MS = 60_000;

/**
 * Admin Dashboard
 * System monitoring view backed by /api/AdminDashboard (8 read-only endpoints).
 * Replaces the previous mock-data dashboard with real backend widgets.
 */
export default function AdminDashboard() {
  const [userStats, setUserStats] = useState(null);
  const [usersByRole, setUsersByRole] = useState([]);
  const [auditSummary, setAuditSummary] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [onlineLoading, setOnlineLoading] = useState(false);
  const [error, setError] = useState("");
  const onlineTimerRef = useRef(null);

  const loadOnline = useCallback(async () => {
    setOnlineLoading(true);
    try {
      const data = await getAdminOnlineUsers();
      setOnlineUsers(Array.isArray(data) ? data : []);
    } catch {
      // Silencioso: el error global ya se muestra en el banner principal y
      // este widget se actualiza por timer — no debe bloquear el dashboard.
    } finally {
      setOnlineLoading(false);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [stats, byRole, audit, recent, online] = await Promise.all([
        getAdminUserStats(),
        getAdminUsersByRole(),
        getAdminAuditSummary24h(),
        getAdminRecentActivity(20),
        getAdminOnlineUsers(),
      ]);
      setUserStats(stats);
      setUsersByRole(byRole);
      setAuditSummary(audit);
      setRecentActivity(recent);
      setOnlineUsers(online);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Could not load dashboard data. Verify your session and try again."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    onlineTimerRef.current = setInterval(loadOnline, ONLINE_REFRESH_MS);
    return () => clearInterval(onlineTimerRef.current);
  }, [loadOnline]);

  return (
    <Stack spacing={3}>
      <DashboardHeader
        title="System Administration"
        subtitle="User management, audit logs, and live session monitoring"
        icon={<AdminPanelSettingsIcon />}
        color="secondary"
      />

      {error && <Alert severity="error">{error}</Alert>}

      <UserStatsWidget stats={userStats} loading={loading} />
      <UsersByRoleWidget rows={usersByRole} loading={loading} />
      <AuditSummaryWidget data={auditSummary} loading={loading} />
      <RecentActivityWidget rows={recentActivity} loading={loading} />
      <OnlineUsersWidget
        rows={onlineUsers}
        loading={onlineLoading || loading}
        onRefresh={loadOnline}
      />
      <InactiveUsersWidget />
    </Stack>
  );
}
